import { useCallback, useEffect, useReducer } from 'react'
import { maskRut } from '@lib/utils/formatRut'
import { clampChilePhone } from '@lib/utils/chilePhone'
import { useTranslation } from 'next-i18next/pages'
import { ToastType, useUI } from '@corratech/pylot-ui/context'
import { useCreateAccount, useAuthUI } from '@corratech/pylot-auth-manager'
import { useCreateAddress } from '@corratech/pylot-address-book/src/hooks/use-create-address'
import { CustomerInput } from '@pylot-data/fwrdschema'
import {
    identifyEvent,
    registrationEvent,
    customerUpdateEvent
} from '@lib/utils/exponeaTrackingEvents'
import {
    RegisterFormData,
    RegisterFormErrors,
    RegisterFormElements
} from '../types'
import { validateForm } from '../utils/registerFormValidator'
import { toSignUpAddressInput } from '../utils/signUpAddressInput'

export const PHONE_CODE = '56'
export const COUNTRY_CODE = 'CL'
export const RUT_FILTER = '11111111-1'
// Oldest selectable birth date for the calendar (YYYY-MM-DD)
export const MIN_BIRTH_DATE = '1900-01-01'

// Today as YYYY-MM-DD, used as the max selectable birth date
const getTodayISODate = (): string => {
    const now = new Date()
    const year = now.getFullYear()
    const month = `${now.getMonth() + 1}`.padStart(2, '0')
    const day = `${now.getDate()}`.padStart(2, '0')
    return `${year}-${month}-${day}`
}

// "YYYY-MM-DD" (native date input value) -> "DD-MM-YYYY" (Magento date_of_birth)
const toMagentoDateOfBirth = (isoDate: string): string => {
    const [year, month, day] = isoDate.split('-')
    return `${day}-${month}-${year}`
}

// "YYYY-MM-DD" -> "DD/MM/YYYY" (human readable, used for tracking)
const toDisplayBirthday = (isoDate: string): string => {
    if (!isoDate) return ''
    const [year, month, day] = isoDate.split('-')
    return `${day}/${month}/${year}`
}

export type SignUpPrefill = {
    email: string
    firstname: string
    lastname: string
}

type RegionValue = { name: string; id: string; code: string }
type ComunaValue = { name: string; id: string }

type StringFormField =
    | 'email'
    | 'firstName'
    | 'lastName'
    | 'phone'
    | 'rut'
    | 'birthday'
    | 'password'
    | 'confirmPassword'
    | 'saveAddressAs'
    | 'address'
    | 'addressNumber'
    | 'addressComplement'
    | 'zipcode'
    | 'addressPhone'

type ToggleField = 'addShippingAddress' | 'acceptOffersByEmail' | 'acceptTerms'

const emptyErrors = {} as RegisterFormErrors

const initialRegion: RegionValue = { name: '', id: '', code: '' }
const initialComuna: ComunaValue = { name: '', id: '' }

type SignUpState = {
    email: string
    firstName: string
    lastName: string
    phone: string
    rut: string
    birthday: string
    password: string
    confirmPassword: string
    addShippingAddress: boolean
    acceptOffersByEmail: boolean
    acceptTerms: boolean
    saveAddressAs: string
    region: RegionValue
    comuna: ComunaValue
    address: string
    addressNumber: string
    addressComplement: string
    zipcode: string
    addressPhone: string
    loading: boolean
    message: string
    dirty: boolean
    disabled: boolean
    formErrors: RegisterFormErrors
}

const initialState: SignUpState = {
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    rut: '',
    birthday: '',
    password: '',
    confirmPassword: '',
    addShippingAddress: false,
    acceptOffersByEmail: false,
    acceptTerms: false,
    saveAddressAs: '',
    region: initialRegion,
    comuna: initialComuna,
    address: '',
    addressNumber: '',
    addressComplement: '',
    zipcode: '',
    addressPhone: '',
    loading: false,
    message: '',
    dirty: false,
    disabled: false,
    formErrors: emptyErrors
}

type Action =
    | { type: 'SET_FIELD'; field: StringFormField; value: string }
    | { type: 'TOGGLE'; field: ToggleField }
    | { type: 'SET_REGION'; region: RegionValue }
    | { type: 'SET_COMUNA'; comuna: ComunaValue }
    | { type: 'SET_FORM_ERRORS'; formErrors: RegisterFormErrors }
    | { type: 'SET_LOADING'; loading: boolean }
    | { type: 'SET_MESSAGE'; message: string }
    | { type: 'SET_DIRTY'; dirty: boolean }
    | { type: 'SET_DISABLED'; disabled: boolean }

function reducer(state: SignUpState, action: Action): SignUpState {
    switch (action.type) {
        case 'SET_FIELD':
            return { ...state, [action.field]: action.value }
        case 'TOGGLE':
            return { ...state, [action.field]: !state[action.field] }
        case 'SET_REGION':
            return { ...state, region: action.region }
        case 'SET_COMUNA':
            return { ...state, comuna: action.comuna }
        case 'SET_FORM_ERRORS':
            return { ...state, formErrors: action.formErrors }
        case 'SET_LOADING':
            return { ...state, loading: action.loading }
        case 'SET_MESSAGE':
            return { ...state, message: action.message }
        case 'SET_DIRTY':
            return { ...state, dirty: action.dirty }
        case 'SET_DISABLED':
            return { ...state, disabled: action.disabled }
        default:
            return state
    }
}

function toRegisterFormData(s: SignUpState): RegisterFormData {
    return {
        email: s.email,
        firstName: s.firstName,
        lastName: s.lastName,
        phone: s.phone,
        rut: s.rut,
        birthday: s.birthday,
        password: s.password,
        confirmPassword: s.confirmPassword,
        addShippingAddress: s.addShippingAddress,
        acceptTerms: s.acceptTerms,
        acceptOffersByEmail: s.acceptOffersByEmail,
        saveAddressAs: s.saveAddressAs,
        region: s.region.name,
        comuna: s.comuna.name,
        address: s.address,
        addressNumber: s.addressNumber,
        addressComplement: s.addressComplement,
        zipcode: s.zipcode,
        addressPhone: s.addressPhone
    }
}

function exponeaCustomerEvents(attributes: Record<string, unknown>) {
    const rut = attributes.rut as string
    identifyEvent({
        registered: rut,
        email_id: attributes.email as string
    })
    registrationEvent(Object.assign({}, attributes))
    customerUpdateEvent(Object.assign({}, attributes))
}

export function useSignUpForm(data?: SignUpPrefill) {
    const { t } = useTranslation(['common'])
    const { createAccount } = useCreateAccount()
    const { createCustomerAddress } = useCreateAddress()
    const { openToast } = useUI()
    const { setModalView, closeAuthModal } = useAuthUI()

    const [state, dispatch] = useReducer(
        reducer,
        data,
        (initialData): SignUpState => {
            if (!initialData) return initialState
            return {
                ...initialState,
                ...(initialData.email && { email: initialData.email }),
                ...(initialData.firstname && {
                    firstName: initialData.firstname
                }),
                ...(initialData.lastname && { lastName: initialData.lastname })
            }
        }
    )

    const setField = useCallback((field: StringFormField, value: string) => {
        dispatch({ type: 'SET_FIELD', field, value })
    }, [])

    const handleBirthdayChange = useCallback((date: string) => {
        // Native <input type="date"> yields an empty string or a valid
        // "YYYY-MM-DD" value, so it can be stored as-is.
        dispatch({ type: 'SET_FIELD', field: 'birthday', value: date })
    }, [])

    const handlePhoneChange = useCallback(
        (value: string, e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = clampChilePhone(value)
            dispatch({ type: 'SET_FIELD', field: 'phone', value: newValue })
            e.target.value = newValue
        },
        []
    )

    const handleAddressPhoneChange = useCallback(
        (value: string, e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = clampChilePhone(value)
            dispatch({
                type: 'SET_FIELD',
                field: 'addressPhone',
                value: newValue
            })
            e.target.value = newValue
        },
        []
    )

    const handleRutChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = maskRut(e.target.value)
            dispatch({ type: 'SET_FIELD', field: 'rut', value: newValue })
            e.target.value = newValue
        },
        []
    )

    const handleValidation = useCallback(() => {
        if (!state.dirty) return
        const { errors, hasErrors } = validateForm(toRegisterFormData(state), t)
        dispatch({ type: 'SET_DISABLED', disabled: hasErrors })
        dispatch({ type: 'SET_FORM_ERRORS', formErrors: errors })
    }, [
        state.dirty,
        state.email,
        state.firstName,
        state.lastName,
        state.phone,
        state.rut,
        state.birthday,
        state.password,
        state.confirmPassword,
        state.addShippingAddress,
        state.acceptTerms,
        state.region,
        state.comuna,
        state.address,
        state.addressNumber,
        state.addressPhone,
        t
    ])

    useEffect(() => {
        handleValidation()
    }, [handleValidation])

    const handleSignup = async (e: React.FormEvent<RegisterFormElements>) => {
        e.preventDefault()
        const { errors, hasErrors } = validateForm(toRegisterFormData(state), t)

        if (!hasErrors) {
            try {
                let document_number = state.rut.replace(/[,|.]/gi, '')
                if (!document_number.includes('-')) {
                    const index = document_number.length - 1
                    document_number = `${document_number.slice(
                        0,
                        index
                    )}-${document_number.slice(index)}`
                }
                const customer = {
                    email: state.email,
                    firstname: state.firstName,
                    lastname: state.lastName,
                    password: state.password,
                    ...(state.birthday && {
                        date_of_birth: toMagentoDateOfBirth(state.birthday)
                    }),
                    is_subscribed: state.acceptOffersByEmail,
                    document_number,
                    customer_telephone: state.phone,
                    customer_prefix_phone: PHONE_CODE,
                    document_type: '1'
                } as CustomerInput

                dispatch({ type: 'SET_LOADING', loading: true })
                dispatch({ type: 'SET_MESSAGE', message: '' })
                await createAccount(customer)

                if (state.addShippingAddress) {
                    const addressResponse = await createCustomerAddress(
                        toSignUpAddressInput(
                            state,
                            document_number,
                            PHONE_CODE,
                            COUNTRY_CODE
                        )
                    )
                    if (addressResponse?.errors?.length) {
                        openToast(
                            t(
                                'account||account|Something went wrong. Please try again'
                            ),
                            ToastType.Warning
                        )
                    }
                }
                const validatedRut =
                    document_number === RUT_FILTER ? ' ' : document_number
                exponeaCustomerEvents({
                    first_name: state.firstName,
                    last_name: state.lastName,
                    email: state.email.toLowerCase(),
                    phone: state.phone,
                    rut: validatedRut,
                    birthday: toDisplayBirthday(state.birthday),
                    opt_in_ofertas: state.acceptOffersByEmail,
                    opt_in_tyc: state.acceptTerms ? 1 : 0,
                    origen: 'web'
                })
                if (state.addShippingAddress) {
                    customerUpdateEvent({
                        comuna: state.comuna.name,
                        region: state.region.name,
                        depto_oficina: state.addressComplement,
                        direccion: state.address,
                        numero: state.addressNumber,
                        codigo_pais: COUNTRY_CODE
                    })
                }
                dispatch({ type: 'SET_LOADING', loading: false })
                closeAuthModal()
                openToast(
                    t('auth|Thank you for registering'),
                    ToastType.Success
                )
            } catch (errors) {
                dispatch({
                    type: 'SET_MESSAGE',
                    message:
                        //@ts-ignore
                        errors?.[0]?.message || t('404|Something went wrong.')
                })
            } finally {
                dispatch({ type: 'SET_LOADING', loading: false })
                dispatch({ type: 'SET_DISABLED', disabled: false })
            }
        } else {
            dispatch({ type: 'SET_DIRTY', dirty: true })
            dispatch({ type: 'SET_DISABLED', disabled: true })
            dispatch({ type: 'SET_FORM_ERRORS', formErrors: errors })
        }
    }

    return {
        t,
        state,
        maxBirthDate: getTodayISODate(),
        setField,
        toggle: (field: ToggleField) => dispatch({ type: 'TOGGLE', field }),
        setRegion: (region: RegionValue) =>
            dispatch({ type: 'SET_REGION', region }),
        setComuna: (comuna: ComunaValue) =>
            dispatch({ type: 'SET_COMUNA', comuna }),
        handleSignup,
        handleBirthdayChange,
        handlePhoneChange,
        handleAddressPhoneChange,
        handleRutChange,
        setModalView
    }
}
